/**
 * localStorage persistence: player name, finished-replay ring buffer, and the
 * in-progress run save (a replay prefix + tick -- resuming re-simulates, so a
 * save can never drift from what actually happened; Pillar 5 x Pillar 2).
 */
import { isReplayShaped } from '../core/replay.js';
import type { Replay } from '../core/types.js';

const NAME_KEY = 'mazetd-name';
const REPLAYS_KEY = 'mazetd-replays';
const RUN_KEY = 'mazetd-run';
const MAX_STASHED = 10;

export interface SavedRun {
  readonly replay: Replay;
  readonly tick: number;
}

export function loadName(): string {
  return localStorage.getItem(NAME_KEY) ?? '';
}

export function saveName(name: string): void {
  localStorage.setItem(NAME_KEY, name);
}

export function stashReplay(replay: Replay): void {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(REPLAYS_KEY) ?? '[]');
    const stash = Array.isArray(raw) ? raw : [];
    stash.unshift(replay);
    localStorage.setItem(REPLAYS_KEY, JSON.stringify(stash.slice(0, MAX_STASHED)));
  } catch {
    // storage full or corrupted stash: losing the local ring buffer is
    // acceptable; the leaderboard copy is the durable one
  }
}

export function saveRun(replay: Replay, tick: number): void {
  try {
    localStorage.setItem(RUN_KEY, JSON.stringify({ replay, tick }));
  } catch {
    // storage full: skip this autosave; the next one retries
  }
}

export function loadRun(): SavedRun | null {
  const raw = localStorage.getItem(RUN_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'replay' in parsed &&
      'tick' in parsed &&
      typeof (parsed as { tick: unknown }).tick === 'number' &&
      isReplayShaped((parsed as { replay: unknown }).replay)
    ) {
      const run = parsed as { replay: Replay; tick: number };
      if (!run.replay.result) return { replay: run.replay, tick: run.tick };
    }
  } catch {
    // fall through: corrupt save is cleared below
  }
  localStorage.removeItem(RUN_KEY);
  return null;
}

export function clearRun(): void {
  localStorage.removeItem(RUN_KEY);
}
